import { useApolloClient } from "@apollo/client";

import { Select } from "@cofano/cck";

import { gql } from "__generated__/gql";
import type { Agreement_Templates_DialogQuery } from "__generated__/graphql";
import { CharterSubtype, CharterType } from "__generated__/enums";
import { ChildPropsFromPagination } from "types/commons";

export type AgreementTemplateSelectOption = ChildPropsFromPagination<Agreement_Templates_DialogQuery, "agreementTemplates">;

type AgreementTemplateSelectProps = {
	value?: AgreementTemplateSelectOption | null;
	onChange: (value: AgreementTemplateSelectOption | null) => void;
	charterType: CharterType;
	charterSubtype: CharterSubtype;
};

const AgreementTemplateSelect = ({ value, onChange, charterType, charterSubtype }: AgreementTemplateSelectProps) => {

	const client = useApolloClient();

	const onFetchOptionsPaginated = async(params: string, page: number) => {
		try  {
			const result = await client.query({
				query: query,
				variables: {
					search: {
						description: params,
						charterTypes: [ charterType ],
						charterSubtypes: [ charterSubtype ],
					},
					pagination: {
						page: page - 1,
						size: 5,
					},
				},
			});

			return {
				options: result.data.agreementTemplates.content ?? [],
				totalResults: result.data.agreementTemplates.totalElements,
			};
		} catch (response) {
			return {
				options: [],
				totalResults: 0,
			};
		}
	};

	return (
		<Select
			fetchOptions={onFetchOptionsPaginated}
			paginated
			getOptionLabel={(option) => option.description || ""}
			onChange={(option) => onChange(option)}
			value={value}
		/>
	);
};

const query = gql(`#graphql
	query agreement_templates_dialog($search: AgreementsTemplateSearch!, $pagination: Pagination!) {
		agreementTemplates(search: $search, pagination: $pagination) {
			content {
				id
				description
				isDefault
				agreements {
					id
					type
					priority
					description
					createdOn
					costType {
						id
						title_de
						title_en
						title_nl
					}
					summary
					... on BlockFreightAgreement {
						days
					}
					... on RentAgreement {
						shipAvailability {
							...availabilityFragment
						}
						workdays
					}
					... on ShipAgreement {
						ships {
							id
							name
						}
					}
					... on WaterwayAgreement {
						location {
							id
							description
							displayCode
							locationGroups {
								id
								description
								displayCode
							}
						}
					}
					calculationMethod {
						id
						type
						price
						minimumPrice
						minimumQuantityPayable
						preAgreed
						payableOver
						description
						customUnit
						... on CargoQuantityCalculationMethod {
							cargoQuantityUnit
							weightRanges {
								from
								to
								price
							}
						}
						... on PercentageOnAgreementsCalculationMethod {
							percentage
							applicableAgreementTypes
							applicableAgreements {
								id
								type
								description
								calculationMethod {
									id
									type
									description
									price
								}
							}
						}
						... on TimeUnitCalculationMethod {
							timeUnit
							minimumTimeUnitsPayable
						}
						... on QuantityUnitCalculationMethod {
							quantityUnit
						}
						... on DemurrageCalculationMethod {
							demurrageTerms
							demurragePayableOver
							timeUnit
							cargoQuantityUnit
							demurrageCountingType
							shortNotice
							startCountingTime
							workingHoursSinceNomination
							workingDaysSinceNomination
							laytimeAllowed
						}
						... on DemurrageCalculationCalculationMethod {
							laytimeRoundingRule
							laytimeRoundingUnit
							laytimeRoundingScope
							laytimePeriodRegime
							demurrageCalculationUnit
							laytimeAllowed
						}
						... on GasoilConsumptionCalculationMethod {
							priceSource
							referenceRateDateType
						}
						... on ReferenceRateCalculationMethod {
							referenceRateUnit
							referenceRateBase
							referenceRateDateType
							route
							loadLocation {
								id
								description
								displayCode
								locationGroups {
									id
									description
									displayCode
								}
							}
							dischargeLocation {
								id
								description
								displayCode
								locationGroups {
									id
									description
									displayCode
								}
							}
							adjustmentType
							adjustmentAmount
							pjkTariff
						}
						... on StaffelCalculationMethod {
							staffelUnit
							staffel {
								...GeneralStaffel
							}
							staffelItem {
								...GeneralStaffelItem
							}
						}
						... on TieredCalculationMethod {
							tierUnit
							cargoQuantityUnit
							referenceRateDateType
							waterLevelLocation
							tiers {
								id
								minimum
								maximum
								priceUnit
								price
								pricePerMtAdditionalToLumpsum
							}
							applicableAgreements {
								id
								type
								description
							}
						}
					}
					conditions {
						id
						type
						... on ContainerFullCondition {
							empty
						}
						... on LocationCondition {
							location {
								id
								description
							}
							locationActionType
						}
						... on ProductCondition {
							product {
								id
								name
								code
							}
						}
						... on RouteCondition {
							fromLocation {
								id
								description
							}
							toLocation {
								id
								description
							}
						}
						... on ShipCondition {
							ship {
								id
								name
							}
						}
						... on WaterLevelCondition {
							waterLevelLocation
							waterLevelMin
							waterLevelMax
						}
						... on ReeferCheckTypeCondition {
							reeferCheckType
						}
					}
				}
			}
			totalElements
		}
	}
`);

export default AgreementTemplateSelect;
